function initInventoryCardGallery(root) {
    if (!root || root.dataset.initialized === 'true') return;

    const track = root.querySelector('[data-inventory-card-gallery-track]');
    const prevButton = root.querySelector('[data-inventory-card-gallery-prev]');
    const nextButton = root.querySelector('[data-inventory-card-gallery-next]');
    const viewMoreLink = root.querySelector('[data-inventory-card-gallery-view-more]');
    const galleryLinks = Array.from(root.querySelectorAll('[data-inventory-card-gallery-link]'));
    const dots = Array.from(root.querySelectorAll('[data-inventory-card-gallery-dot]'));
    const slides = Array.from(track ? track.children : []);
    const hasMoreImages = !!viewMoreLink;

    if (!track || !slides.length) {
        root.dataset.initialized = 'true';
        return;
    }

    const state = {
        activeIndex: 0,
        movedDuringGesture: false,
        pointer: null,
        isDragging: false,
        programmaticTargetIndex: null
    };

    const getPlaceholderImage = function (slide) {
        return slide && slide.dataset ? slide.dataset.placeholderImage : null;
    };

    const loadSlide = function (index) {
        const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
        if (
            !slide
            || slide.dataset.inventoryCardGalleryImageLoaded === 'true'
            || slide.dataset.inventoryCardGalleryImageLoading === 'true'
            || slide.classList.contains('lozad--loaded')
        ) return;

        const backgroundImage = slide.dataset.backgroundImage;
        const placeholderImage = getPlaceholderImage(slide);

        if (!backgroundImage) {
            if (placeholderImage) slide.style.backgroundImage = `url('${placeholderImage}')`;
            slide.dataset.inventoryCardGalleryImageLoaded = 'true';
            slide.classList.add('lozad--loaded');
            return;
        }

        slide.style.backgroundImage = `url('${backgroundImage}')`;
        slide.dataset.inventoryCardGalleryImageLoaded = 'true';
        slide.classList.add('lozad--loaded');
    };

    const loadImageRange = function (index, includeLookahead) {
        const boundedIndex = Math.max(0, Math.min(index, slides.length - 1));
        loadSlide(boundedIndex);

        if (includeLookahead) {
            loadSlide(Math.min(boundedIndex + 1, slides.length - 1));
        }
    };

    const observeFirstSlide = function () {
        const firstSlide = slides[0];
        if (!firstSlide) return;

        if (!('IntersectionObserver' in window)) {
            loadImageRange(0, true);
            return;
        }

        const firstSlideObserver = new IntersectionObserver(function (entries) {
            if (!entries[0] || !entries[0].isIntersecting) return;
            loadImageRange(0, true);
            firstSlideObserver.disconnect();
        }, {
            rootMargin: '480px 0px',
            threshold: 0.1
        });

        firstSlideObserver.observe(firstSlide);
    };

    observeFirstSlide();

    if (slides.length <= 1) {
        root.dataset.initialized = 'true';
        return;
    }

    const setActiveIndex = function (index) {
        state.activeIndex = Math.max(0, Math.min(index, slides.length - 1));

        if (prevButton) {
            prevButton.dataset.enabled = state.activeIndex > 0 ? 'true' : 'false';
            prevButton.disabled = state.activeIndex === 0;
        }

        if (nextButton) {
            const isAtLastVisibleImage = state.activeIndex === slides.length - 1;
            nextButton.dataset.enabled = state.activeIndex < slides.length - 1 ? 'true' : 'false';
            nextButton.disabled = isAtLastVisibleImage;
            nextButton.setAttribute('aria-hidden', hasMoreImages && isAtLastVisibleImage ? 'true' : 'false');
        }

        if (viewMoreLink) {
            viewMoreLink.classList.toggle('is-visible', state.activeIndex === slides.length - 1);
        }

        root.dataset.trailingControl = hasMoreImages && state.activeIndex === slides.length - 1 ? 'view-more' : 'next';

        dots.forEach(function (dot, dotIndex) {
            const isActive = dotIndex === state.activeIndex;
            dot.classList.toggle('is-active', isActive);
            if (isActive) {
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.removeAttribute('aria-current');
            }
        });
    };

    const scrollToIndex = function (index) {
        const boundedIndex = Math.max(0, Math.min(index, slides.length - 1));
        const targetSlide = slides[boundedIndex];

        if (!targetSlide) return;

        loadImageRange(boundedIndex, true);
        state.programmaticTargetIndex = boundedIndex;
        track.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: state.isDragging ? 'auto' : 'smooth'
        });

        setActiveIndex(boundedIndex);
    };

    const updateFromScroll = function () {
        if (state.programmaticTargetIndex !== null) {
            const pendingSlide = slides[state.programmaticTargetIndex];
            const pendingOffset = pendingSlide ? pendingSlide.offsetLeft : 0;

            if (Math.abs(track.scrollLeft - pendingOffset) <= 2) {
                state.programmaticTargetIndex = null;
            } else {
                return;
            }
        }

        const nextIndex = slides.reduce(function (closestIndex, slide, index) {
            const closestSlide = slides[closestIndex];
            const currentDistance = Math.abs((closestSlide ? closestSlide.offsetLeft : 0) - track.scrollLeft);
            const nextDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
            return nextDistance < currentDistance ? index : closestIndex;
        }, 0);

        if (nextIndex !== state.activeIndex) {
            loadImageRange(nextIndex, true);
            setActiveIndex(nextIndex);
        }
    };

    const clearPointerState = function () {
        state.pointer = null;
        state.isDragging = false;
        root.classList.remove('is-dragging');

        window.setTimeout(function () {
            state.movedDuringGesture = false;
        }, 0);
    };

    track.addEventListener('scroll', updateFromScroll, {passive: true});

    root.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button') || event.target.closest('[data-inventory-card-gallery-view-more]')) return;

        loadImageRange(state.activeIndex + 1, false);
        state.programmaticTargetIndex = null;
        state.pointer = {
            id: event.pointerId,
            type: event.pointerType,
            startX: event.clientX,
            startY: event.clientY,
            startScrollLeft: track.scrollLeft
        };
        state.movedDuringGesture = false;
    });

    root.addEventListener('pointermove', function (event) {
        if (!state.pointer || state.pointer.id !== event.pointerId) return;

        const deltaX = event.clientX - state.pointer.startX;
        const deltaY = event.clientY - state.pointer.startY;

        if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
            state.movedDuringGesture = true;
        }

        if (state.pointer.type !== 'mouse') return;
        if (!state.isDragging && Math.abs(deltaX) <= 8) return;

        if (!state.isDragging) {
            state.isDragging = true;
            root.classList.add('is-dragging');
            if (root.setPointerCapture) {
                root.setPointerCapture(event.pointerId);
            }
        }

        event.preventDefault();
        track.scrollLeft = state.pointer.startScrollLeft - deltaX;
    });

    root.addEventListener('pointerup', function (event) {
        if (!state.pointer || state.pointer.id !== event.pointerId) return;

        if (state.pointer.type === 'mouse' && state.isDragging && root.releasePointerCapture) {
            root.releasePointerCapture(event.pointerId);
        }

        clearPointerState();
    });

    galleryLinks.forEach(function (galleryLink) {
        galleryLink.addEventListener('dragstart', function (event) {
            event.preventDefault();
        });

        galleryLink.addEventListener('click', function (event) {
            if (!state.movedDuringGesture) return;
            event.preventDefault();
            event.stopPropagation();
        });
    });

    root.addEventListener('pointercancel', clearPointerState);
    root.addEventListener('pointerleave', function (event) {
        if (state.pointer && state.pointer.type === 'mouse' && event.buttons === 0) {
            clearPointerState();
        }
    });

    if (prevButton) {
        prevButton.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            loadImageRange(state.activeIndex - 1, true);
            scrollToIndex(state.activeIndex - 1);
        });
    }

    if (nextButton) {
        nextButton.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (state.activeIndex === slides.length - 1) return;
            loadImageRange(state.activeIndex + 1, true);
            scrollToIndex(state.activeIndex + 1);
        });
    }

    dots.forEach(function (dot) {
        dot.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            const nextIndex = parseInt(dot.dataset.index, 10) || 0;
            loadImageRange(nextIndex, true);
            scrollToIndex(nextIndex);
        });
    });

    root.addEventListener('mouseenter', function () {
        loadImageRange(state.activeIndex + 1, false);
    });

    root.addEventListener('focusin', function () {
        loadImageRange(state.activeIndex + 1, false);
    });

    setActiveIndex(0);
    updateFromScroll();
    root.dataset.initialized = 'true';
}

function initAllInventoryCardGalleries() {
    document.querySelectorAll('[data-inventory-card-gallery]').forEach(initInventoryCardGallery);
}

initAllInventoryCardGalleries();

const observer = new MutationObserver(function () {
    initAllInventoryCardGalleries();
});

if (document.body) {
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}
